<template>
  <div
    class="betting-pick-action"
    :class="[
      `betting-pick-action--${platform}`,
      `betting-pick-action--${variant}`,
      { 'betting-pick-action--has-menu': showDropdown },
      { 'betting-pick-action--active': active }
    ]"
  >
    <button
      type="button"
      class="betting-pick-action__main"
      @click="emit('action', selectedCount)"
    >
      <span
        class="betting-action-btn-icon"
        aria-hidden="true"
      >
        <slot name="icon" />
      </span>
      <span class="betting-pick-action__label"><slot /></span>
    </button>

    <div
      v-if="showDropdown"
      ref="dropdownRef"
      class="betting-pick-action__menu-wrap"
    >
      <button
        ref="toggleRef"
        type="button"
        class="betting-pick-action__toggle"
        :aria-expanded="menuOpen"
        :aria-label="props.menuLabel ?? 'Select number of balls'"
        @click.stop="toggleMenu"
      >
        <span class="betting-pick-action__count">{{ selectedCount }}</span>
        <svg
          viewBox="0 0 12 8"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M1 1.5L6 6.5L11 1.5"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>

      <Teleport to="body">
        <div
          v-if="menuOpen"
          ref="menuRef"
          class="betting-pick-action__menu"
          :class="`betting-pick-action__menu--${variant}`"
          :style="menuPositionStyle"
        >
          <button
            v-for="count in pickOptions"
            :key="count"
            type="button"
            class="betting-pick-action__menu-item"
            :class="{ active: count === selectedCount }"
            @click="selectCount(count)"
          >
            {{ count }}
          </button>
        </div>
      </Teleport>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch, withDefaults } from 'vue'

interface Props {
  variant: 'quick' | 'hot' | 'cold'
  platform: 'desktop' | 'mobile'
  pickOptions: number[]
  selectedCount: number
  menuLabel?: string
  active?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  active: false,
})

const emit = defineEmits<{
  (e: 'action', count: number): void
  (e: 'update:selectedCount', count: number): void
}>()

const menuOpen = ref(false)
const dropdownRef = ref<HTMLElement | null>(null)
const toggleRef = ref<HTMLElement | null>(null)
const menuRef = ref<HTMLElement | null>(null)
const menuPositionStyle = ref<Record<string, string>>({})

const showDropdown = computed(() => props.pickOptions.length >= 1)

const positionMenu = () => {
  const anchor = toggleRef.value ?? dropdownRef.value
  if (!anchor) return

  const rect = anchor.getBoundingClientRect()
  menuPositionStyle.value = {
    position: 'fixed',
    top: `${rect.top - 6}px`,
    left: `${rect.right}px`,
    transform: 'translate(-100%, -100%)',
    zIndex: '10000',
  }
}

const closeMenu = () => {
  menuOpen.value = false
}

const toggleMenu = async () => {
  menuOpen.value = !menuOpen.value
  if (menuOpen.value) {
    await nextTick()
    positionMenu()
  }
}

const handleReposition = () => {
  if (menuOpen.value) {
    positionMenu()
  }
}

const selectCount = (count: number) => {
  emit('update:selectedCount', count)
  emit('action', count)
  closeMenu()
}

const handleDocumentClick = (event: MouseEvent) => {
  if (!menuOpen.value) return
  const target = event.target as Node
  if (dropdownRef.value?.contains(target)) return
  if (menuRef.value?.contains(target)) return
  closeMenu()
}

watch(() => props.pickOptions, () => {
  closeMenu()
})

onMounted(() => {
  document.addEventListener('click', handleDocumentClick)
  window.addEventListener('scroll', handleReposition, true)
  window.addEventListener('resize', handleReposition)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleDocumentClick)
  window.removeEventListener('scroll', handleReposition, true)
  window.removeEventListener('resize', handleReposition)
})
</script>

<style scoped>
.betting-pick-action {
  display: flex;
  align-items: stretch;
  min-width: 0;
  min-height: 44px;
  border-radius: 12px;
  border: 1px solid var(--detail-secondary-action-border, var(--bg-secondary-button-border-color, var(--colour-surface-cont)));
  background: var(--colour-surface-cont-lowest);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.16),
    0 4px 12px color-mix(in srgb, var(--shadow-color) 26%, transparent);
  overflow: visible;
}

.betting-pick-action--mobile {
  min-height: 42px;
}

.betting-pick-action__main {
  flex: 1;
  min-width: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 11px 10px;
  border: 0;
  border-radius: 12px 0 0 12px;
  background: transparent;
  color: var(--detail-secondary-action-color, var(--bg-secondary-button-color, var(--colour-base-text-colour)));
  font-size: 14px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: color 0.15s ease, transform 0.15s ease;
}

.betting-pick-action--mobile .betting-pick-action__main {
  font-size: 13px;
  padding: 10px 8px;
}

.betting-pick-action:not(.betting-pick-action--has-menu) .betting-pick-action__main {
  border-radius: 12px;
  width: 100%;
}

.betting-pick-action__label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.betting-pick-action__menu-wrap {
  position: relative;
  flex-shrink: 0;
  align-self: stretch;
}

.betting-pick-action__toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  min-width: 38px;
  height: 100%;
  min-height: 44px;
  padding: 0 8px;
  border: 0;
  border-left: 1px solid var(--detail-secondary-action-border, var(--bg-secondary-button-border-color, var(--colour-surface-cont)));
  border-radius: 0 12px 12px 0;
  background: color-mix(in srgb, var(--colour-surface-cont-low) 18%, var(--colour-surface-cont-lowest) 82%);
  color: inherit;
  cursor: pointer;
}

.betting-pick-action__toggle svg {
  width: 10px;
  height: 10px;
}

.betting-pick-action--mobile .betting-pick-action__toggle {
  min-height: 42px;
}

.betting-pick-action__count {
  font-size: 12px;
  font-weight: 700;
  min-width: 12px;
  text-align: center;
}

.betting-pick-action--hot {
  color: var(--trend-hot-text, var(--trend-hot-base));
}

.betting-pick-action--cold {
  color: var(--trend-cold-text, var(--detail-trend-cold-base, #2b7cd3));
}

.betting-pick-action--quick .betting-pick-action__main:hover {
  color: color-mix(in srgb, var(--colour-orange-button) 78%, var(--colour-base-text-colour) 22%);
}

.betting-pick-action--hot .betting-pick-action__main:hover {
  color: color-mix(in srgb, var(--trend-hot-base) 86%, var(--colour-base-text-colour) 14%);
}

.betting-pick-action--cold .betting-pick-action__main:hover {
  color: color-mix(in srgb, var(--trend-cold-text, var(--detail-trend-cold-base, #2b7cd3)) 86%, var(--colour-base-text-colour) 14%);
}

.betting-pick-action--quick.betting-pick-action--active {
  border-color: color-mix(in srgb, var(--colour-orange-button) 55%, var(--colour-surface-cont) 45%);
  background: color-mix(in srgb, var(--colour-orange-button) 14%, var(--colour-surface-cont-lowest) 86%);
  color: var(--colour-orange-button);
}

.betting-pick-action--hot.betting-pick-action--active {
  border-color: color-mix(in srgb, var(--trend-hot-base) 55%, var(--colour-surface-cont) 45%);
  background: color-mix(in srgb, var(--trend-hot-base) 14%, var(--colour-surface-cont-lowest) 86%);
  color: var(--trend-hot-base);
}

.betting-pick-action--cold.betting-pick-action--active {
  border-color: color-mix(in srgb, var(--trend-cold-text, var(--detail-trend-cold-base, #2b7cd3)) 55%, var(--colour-surface-cont) 45%);
  background: color-mix(in srgb, var(--trend-cold-text, var(--detail-trend-cold-base, #2b7cd3)) 14%, var(--colour-surface-cont-lowest) 86%);
  color: var(--trend-cold-text, var(--detail-trend-cold-base, #2b7cd3));
}

.betting-pick-action--active .betting-pick-action__toggle {
  background: color-mix(in srgb, currentColor 10%, var(--colour-surface-cont-lowest) 90%);
}

.betting-pick-action:hover {
  transform: translateY(-1px);
}

.betting-action-btn-icon {
  width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.betting-action-btn-icon :deep(svg) {
  width: 16px;
  height: 16px;
}
</style>

<style>
.betting-pick-action__menu {
  min-width: 52px;
  max-height: 220px;
  overflow-y: auto;
  padding: 4px;
  border-radius: 8px;
  border: 1px solid var(--colour-surface-cont, #dbdbdb);
  background: var(--colour-surface-cont-lowest, #ffffff);
  box-shadow: 0 8px 20px color-mix(in srgb, var(--shadow-color, rgba(0, 0, 0, 0.2)) 28%, transparent);
}

.betting-pick-action__menu-item {
  display: block;
  width: 100%;
  padding: 8px 12px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--colour-base-text-colour, #000000);
  font-family: var(--typography-font-family, 'Ubuntu', sans-serif);
  font-size: 13px;
  font-weight: 600;
  text-align: center;
  cursor: pointer;
}

.betting-pick-action__menu-item:hover,
.betting-pick-action__menu-item.active {
  background: color-mix(in srgb, var(--colour-orange-button, #ff3304) 12%, var(--colour-surface-cont-lowest, #ffffff) 88%);
  color: var(--colour-base-text-colour, #000000);
}

.betting-pick-action__menu--hot .betting-pick-action__menu-item.active,
.betting-pick-action__menu--hot .betting-pick-action__menu-item:hover {
  color: var(--detail-trend-hot-base, var(--colour-yellow-text-colour, #fe9001));
}

.betting-pick-action__menu--cold .betting-pick-action__menu-item.active,
.betting-pick-action__menu--cold .betting-pick-action__menu-item:hover {
  color: var(--detail-trend-cold-base, #2b7cd3);
}
</style>
